import { Platform } from 'react-native';
import Constants, { ExecutionEnvironment } from 'expo-constants';
import * as Application from 'expo-application';
import { RewriteFrames } from '@sentry/integrations';
import { Integration, SdkInfo } from '@sentry/types';

import { ExpoBareIntegration } from './integrations/bare';
import { ExpoManagedIntegration } from './integrations/managed';
import * as Updates from './integrations/Updates'
import { SentryExpoNativeOptions, overrideDefaultIntegrations } from './utils';
import {
  SENTRY_EXPO_PACKAGE,
  SENTRY_EXPO_VERSION,
  SENTRY_REACT_NATIVE_PACKAGE,
} from './version';

import {
  init as initNative,
  Integrations,
  SDK_VERSION as SENTRY_REACT_NATIVE_VERSION,
} from '@sentry/react-native';
import { AppManifest } from 'expo-constants/build/Constants.types';

export * as Native from '@sentry/react-native';

const defaultSdkInfo: SdkInfo = {
  name: 'sentry.javascript.react-native.expo',
  packages: [
    {
      name: SENTRY_EXPO_PACKAGE,
      version: SENTRY_EXPO_VERSION,
    },
    {
      name: SENTRY_REACT_NATIVE_PACKAGE,
      version: SENTRY_REACT_NATIVE_VERSION,
    }
  ],
  version: SENTRY_EXPO_VERSION,
};

const MANIFEST = Updates.manifest as AppManifest;
const IS_BARE_WORKFLOW = Constants.executionEnvironment === ExecutionEnvironment.Bare;

const getDefaultOptions = () => ({
  enableNativeNagger: false, // Otherwise this will trigger an Alert(), let's rely on the logs instead
  release: getDefaultRelease(),
  dist: getDist(),
  ...(IS_BARE_WORKFLOW ? {} : { enableNative: false, enableNativeCrashHandling: false }),
  _metadata: {
    sdk: defaultSdkInfo,
  },
});

/**
 * For embedded updates, the dist version needs to match what is set by the Sentry build script.
 * For modern manifest OTA updates, the updateId is used.
 */
function getDist(): string | null | undefined {
  if (Updates.isEmbeddedLaunch) {
    return MANIFEST.revisionId ? MANIFEST.version : `${Application.nativeBuildVersion}`;
  } else {
    return Updates.updateId;
  }
}

/**
 * We assign the appropriate release based on if the app is running in development,
 * on an Classic OTA Update, or on a no-publish build.
 */
function getDefaultRelease(): string {
  if (__DEV__) {
    return 'DEVELOPMENT';
  } else if (MANIFEST.revisionId) {
    // Want to make sure this still exists in EAS update: equal on iOS & Android
    return MANIFEST.revisionId;
  } else {
    // This is the default set by Sentry's native Xcode & Gradle scripts
    return `${Application.applicationId}@${Application.nativeApplicationVersion}+${Application.nativeBuildVersion}`;
  }
}

export const init = (options: SentryExpoNativeOptions = {}) => {
  const defaultExpoIntegrations = [
    new Integrations.ReactNativeErrorHandlers({
      onerror: false,
      onunhandledrejection: true,
    }),
    IS_BARE_WORKFLOW ? new ExpoBareIntegration() : new ExpoManagedIntegration(),
    new RewriteFrames({
      iteratee: (frame) => {
        if (frame.filename && frame.filename !== '[native code]') {
          frame.filename =
            Platform.OS === 'android' ? 'app:///index.android.bundle' : 'app:///main.jsbundle';
        }
        return frame;
      },
    }),
  ];

  let nativeOptions = { ...getDefaultOptions(), ...options } as SentryExpoNativeOptions;

  if (Array.isArray(nativeOptions.integrations)) {
    // Allow users to override Expo defaults...ymmv
    nativeOptions.integrations = overrideDefaultIntegrations(
      defaultExpoIntegrations,
      nativeOptions.integrations
    );
  } else if (typeof nativeOptions.integrations === 'function') {
    // Need to rewrite the function to take Expo's default integrations
    let functionWithoutExpoIntegrations = nativeOptions.integrations;
    const functionWithExpoIntegrations = (integrations: Integration[]) => {
      return functionWithoutExpoIntegrations(
        overrideDefaultIntegrations(integrations, defaultExpoIntegrations)
      );
    };
    nativeOptions.integrations = functionWithExpoIntegrations;
  } else {
    nativeOptions.integrations = [...defaultExpoIntegrations];
  }

  if (__DEV__ && !nativeOptions.enableInExpoDevelopment) {
    nativeOptions.enabled = false;
    if (!nativeOptions.hasOwnProperty('enableInExpoDevelopment')) {
      console.log(
        '[sentry-expo] Disabled Sentry in development. Note you can set Sentry.init({ enableInExpoDevelopment: true });'
      );
    }
  }

  try {
    return initNative({ ...nativeOptions });
  } catch (e) {
    if (IS_BARE_WORKFLOW) {
      // Native projects have not been linked, try to continue with no native capability
      console.warn(
        `[sentry-expo] Disabling the Sentry Native SDK (all JS errors will still be reported).\nTo enable it, run 'npx expo install @sentry/react-native' in your project directory. To silence this warning, pass 'enableNative: false' to Sentry.init().`
      );
      return initNative({
        ...nativeOptions,
        enableNative: false,
        enableNativeCrashHandling: false,
      });
    } else {
      throw e;
    }
  }
};
